<script lang="ts">
    import type { Price, SaleTags } from '@shared/types/types';
    import BenefitTag from '@tile/components/benefit-tag/BenefitTag.svelte';
    import DealHighlight from '@tile/components/deal-highlight/DealHighlight.svelte';
    import Installments from '@tile/components/installments/Installments.svelte';
    import PriceRow from '@tile/components/price-row/PriceRow.svelte';
    import PriceContainer from '@tile/components/price/Price.svelte';
    import RetailPrice from '@tile/components/retail-price/RetailPrice.svelte';
    import SaleTag from '@tile/components/sale-tag/SaleTag.svelte';
    import SaleTagsComponent from '@tile/components/sale-tags/SaleTags.svelte';
    import SuggestedRetailPrice from '@tile/components/suggested-retail-price/SuggestedRetailPrice.svelte';
    import UnitPrice from '@tile/components/unit-price/UnitPrice.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';
    import PricingItem from '@tilelist/layouts/experiments/e3892/pricing-item/PricingItem.svelte';

    interface Props {
        price: Price;
        saleTags?: SaleTags;
        dealHighlight?: string;
        dealId?: string;
        variationId: string;
        rule?: string;
        showInstallments?: boolean;
        bestVariationBenefit?: string;
    }

    let {
        price,
        saleTags,
        dealHighlight,
        dealId,
        variationId,
        rule,
        showInstallments: isInstallmentEligible = false,
        bestVariationBenefit,
    }: Props = $props();

    const onex = injectWiring().onex;

    const effectiveSaleTags = $derived(saleTags);
    const showBenefitTag = $derived(!onex.isToggleActive('EMERGENCY_DISABLE_BENEFITS'));
    const effectiveBestVariationBenefit = $derived(showBenefitTag ? bestVariationBenefit : undefined);
    const saleTagsEmpty = $derived(!effectiveSaleTags?.discount && !effectiveBestVariationBenefit);
    const showPricingComponent = $derived(!onex.isExperimentGroup('PRICING_COMPONENT', 'E3892A'));

    const e3949Mapping: Record<ExperimentGroups['INSTALLMENT_WARNING'], { installments: boolean; warning: boolean }> = {
        E3949A: { installments: true, warning: false },
        E3949B: { installments: true, warning: true },
        E3949C: { installments: false, warning: false },
    };

    const e3949Variant = $derived(onex.matchGroup('INSTALLMENT_WARNING', e3949Mapping));
    const displayInstallments = $derived(isInstallmentEligible && e3949Variant.installments);
    const displayInstallmentWarning = $derived(displayInstallments && e3949Variant.warning);
</script>

{#snippet installments()}
    {#if displayInstallments}
        <Installments installmentPlan={price.installmentPlan} showWarning={displayInstallmentWarning} />
    {/if}
{/snippet}

{#snippet priceContent()}
    <DealHighlight {dealHighlight} />
    <UnitPrice unitPrice={price.normPrice} />
    {@render installments()}
    <SaleTagsComponent isEmpty={saleTagsEmpty}>
        <SaleTag discount={effectiveSaleTags?.discount} />
        {#if showBenefitTag}
            <BenefitTag bestVariationBenefit={effectiveBestVariationBenefit} {rule} {variationId} />
        {/if}
    </SaleTagsComponent>
{/snippet}

{#if showPricingComponent}
    <PricingItem
        {price}
        deal={dealHighlight || dealId ? { highlight: dealHighlight, dealId } : undefined}
        {variationId}
        {rule}
        {showBenefitTag}
        bestVariationBenefit={effectiveBestVariationBenefit}
    >
        {@render installments()}
    </PricingItem>
{:else}
    <PriceContainer>
        <PriceRow>
            <RetailPrice {price} discounted={price.sale} />
            <SuggestedRetailPrice {price} />
        </PriceRow>
        {@render priceContent()}
    </PriceContainer>
{/if}
